import React, {useRef, useState} from 'react'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import Breadcrumb, {BreadcrumbType} from '../../components/Breadcrumb'
import {
  AssetsBox,
  AssetTitle,
  IconsBox,
  LeftBox,
  LoggedOutBox,
  ObjectiveText,
  PlayerBox,
  RightBox,
  TimeBox,
  TranscriptBox,
} from '../VideoPage/styles'
import {v4 as uuidv4} from 'uuid'
import {Link as RouterLink} from 'react-router-dom'
import ClockIcon from '../../../images/icons/new-dashboard/clock-icon.svg'
import {getDivisionDetails} from '../../../constants/divisions'
import Cookies from 'universal-cookie'
import {useSelector} from 'react-redux'
import Loading from '../../../components/Loading'
import {LoadingStatuses} from '../../../components/types/LoadingStatusses'
import convertDuration from '../../../utils/convertDuration'
import {useAppDispatch} from '../../../redux/configureStore'
import {selectMember} from '../../../redux/members'
import {useAuth} from '../../../components/AuthProvider'
import LockIcon from '@mui/icons-material/Lock'
import {useWindowDimensions} from '../../../hooks/useWindowDimensions'
import {
  selectWorkshopReplayStatus,
  WorkshopReplay,
  WorkshopReplayGroup,
} from '../../../redux/workshopReplay'
import {LessonPlan} from '../../../redux/lessons'
import {communityUrls} from '../../../constants/community-urls'
import {formatDuration} from 'date-fns'
import {Link, Typography} from '@mui/material'
import {colors} from '../../../styles/theme'
import VirtualWorkshopDemoModal from '../../../components/DashboardContent/VirtualWorkshopDemoModal'
import {formatDateToLongString} from '../../../utils/formatDate'
import {Button} from '../../../components/button'
import WorkshopReplaySidenav from '../../components/WorkshopReplaySidenav'
import {formatSeconds} from '../../../utils/hoursMinutes'
import {handleUpgrade} from '../../../utils/upgradeHelper'

type Props = {
  workshopReplayId: string
  replay?: WorkshopReplay
  group?: WorkshopReplayGroup
  lessonPlan?: LessonPlan
  hasAccess?: boolean
  onUpdate: (videoId: string, value: number) => void
}

const View = ({
  workshopReplayId,
  replay,
  group,
  lessonPlan,
  hasAccess,
  onUpdate,
}: Props) => {
  const dispatch = useAppDispatch()
  const auth = useAuth()
  const {isMobile} = useWindowDimensions()

  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(isMobile ? false : true)

  const replayStatus = useSelector(selectWorkshopReplayStatus)
  const member = useSelector(selectMember)

  const breadcrumbs: BreadcrumbType[] = [
    {
      title: replay?.title ?? '',
      url: `#`,
    },
  ]

  const division = getDivisionDetails(
    (replay?.abbr as 'PcM' | 'PjM' | 'PA' | 'PPD' | 'PDD' | 'CE') ?? null,
  )

  const cookies = new Cookies()
  const playerRef: any = useRef()

  let nextEvent = 10

  const onSuccess = (player: any) => {
    const myPlayer = player.ref
    // EVENT THROWN WHEN THE VIDEO STARTS
    myPlayer.on('loadstart', function () {
      myPlayer.currentTime(0)

      myPlayer.playbackRate(Number(cookies.get('bksPlaybackRate')) || 1)

      const savedVolume = cookies.get('bksVolume')
      const savedMuted = cookies.get('bksMuted')
      if (savedVolume !== undefined) {
        myPlayer.volume(Number(savedVolume))
      }
      if (savedMuted !== undefined) {
        myPlayer.muted(savedMuted === 'true')
      }
    })

    const saveVolumeAndMute = () => {
      cookies.set('bksVolume', myPlayer.volume(), {path: '/'})
      cookies.set('bksMuted', myPlayer.muted(), {path: '/'})
    }

    // EVENT THROWN WHEN THE VOLUME CHANGES
    myPlayer.on('volumechange', saveVolumeAndMute)
    myPlayer.on('muted', saveVolumeAndMute)
    myPlayer.on('unmuted', saveVolumeAndMute)

    //EVENT THROWN EVERY SECOND WHILE THE VIDEO IS PLAYING
    myPlayer.on('timeupdate', function () {
      const time = myPlayer.currentTime()
      const playerDuration = Math.floor(myPlayer.duration())

      const link = document.querySelector(
        '#transcript .line[data-time="' + Math.round(time) + '"]',
      )
      if (link) {
        const lines = document.querySelectorAll('#transcript .line')
        lines.forEach(function (line) {
          line.classList.remove('transcriptActive')
        })

        link.classList.add('transcriptActive')
      }

      const percent = Math.round((time / playerDuration) * 100)
      if (percent % 10 == 0) {
        if (percent >= nextEvent) {
          updateProgress(percent)
        }
      }

      const playbackRate = myPlayer.playbackRate()
      if (playbackRate !== cookies.get('bksPlaybackRate')) {
        cookies.set('bksPlaybackRate', playbackRate, {
          path: '/',
        })
      }
    })

    let initialSetupDone = false

    const setCaptions = (enabled: any) => {
      const tracks = myPlayer.textTracks()
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
          tracks[i].mode = enabled ? 'showing' : 'hidden'
        } else {
          tracks[i].mode = 'disabled'
        }
      }
    }

    myPlayer.on('loadedmetadata', () => {
      const captionsEnabled = cookies.get('captionsEnabled') === 'true'
      setCaptions(captionsEnabled)
      initialSetupDone = true
    })

    myPlayer.textTracks().addEventListener('change', () => {
      if (initialSetupDone) {
        const tracks = myPlayer.textTracks()
        let captionsEnabled = false
        for (let i = 0; i < tracks.length; i++) {
          if (tracks[i].kind === 'captions' || tracks[i].kind === 'subtitles') {
            if (tracks[i].mode === 'showing') {
              captionsEnabled = true
              break
            }
          }
        }
        cookies.set('captionsEnabled', String(captionsEnabled), {
          path: '/',
        })
      }
    })
  }

  const getNextWorkshopPrerequisitesApproximateDuration = (
    preReq?: string[],
  ): string => {
    if (lessonPlan && preReq) {
      const videoAssets = lessonPlan.assets.filter(
        asset => asset.assetTypeAbbr === 'video',
      )

      const approximateDuration = preReq.reduce((acc, cur) => {
        const prereqId = cur?.split('video/')[1]?.trim()

        const asset = videoAssets.find(lesson => lesson.id === prereqId)

        if (asset) {
          return acc + convertDuration(asset.videoDuration!)
        }

        return acc
      }, 250) // "Getting Started with Virtual Workshops" duration

      return `approx. ${formatDuration(
        {
          hours: Math.floor(approximateDuration / 3600),
          minutes: Math.floor((approximateDuration % 3600) / 60),
          seconds: Math.floor(approximateDuration % 60),
        },
        {delimiter: '; '},
      )}`
    }

    return ''
  }

  const formatPrereqURL = (url: string) => {
    if (url) {
      const preqReqParts = url.split('- ')
      if (preqReqParts.length === 3) {
        return preqReqParts[2]
      }

      return preqReqParts[1]
    }
  }

  const formatPrereq = (prereq?: string) => {
    if (!prereq) return

    let title
    const preqReqParts = prereq.split('- ')

    if (preqReqParts.length === 3) {
      title = preqReqParts[0] + preqReqParts[1]
    } else {
      title = preqReqParts[0]
    }

    return title
  }

  let workshopPrereq: string[] = []

  if (replay && auth.isAuthenticated) {
    const prereq = replay?.prerequisites
    const idx = prereq?.indexOf('\n2.0')

    if (idx === -1) {
      workshopPrereq = [...prereq.split('\n')]
    } else {
      const version2Prereq = prereq?.slice(idx)
      const prereq2 = version2Prereq.split('\n2.0').slice(1)
      workshopPrereq = [...prereq2]
    }
  }

  const handleDemoClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    event.preventDefault()
    setDemoModalOpen(true)
  }

  const redirectToCommunity = () => {
    if (replay) {
      const url =
        communityUrls[
          replay.abbr as 'PcM' | 'PjM' | 'PA' | 'PPD' | 'PDD' | 'CE'
        ]
      window.open(url, '_blank')
    }
  }

  const updateProgress = (percent: number) => {
    nextEvent = percent + 10
    if (replay) {
      onUpdate(replay.videoId, percent)
    }
  }

  return (
    <>
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <AssetsBox>
        <LeftBox $isExpanded={isExpanded}>
          <WorkshopReplaySidenav
            workshopReplayId={workshopReplayId}
            group={group}
            isExpanded={isExpanded}
            onChangeIsExpanded={(value: boolean) => setIsExpanded(value)}
          />
        </LeftBox>
        <RightBox>
          {hasAccess ? (
            <>
              {replayStatus === LoadingStatuses.Idle ||
              replayStatus === LoadingStatuses.Loading ? (
                <Loading />
              ) : (
                <>
                  <IconsBox>
                    <img src={division?.icon} alt={division?.name} />
                    <AssetTitle style={{marginTop: 0}}>
                      {replay?.title}
                    </AssetTitle>
                  </IconsBox>
                  <ObjectiveText>
                    {formatDateToLongString(replay?.date)}
                    <br />
                    <br />
                    Instructor: {replay?.coach}
                  </ObjectiveText>
                  {replay?.videoDuration && (
                    <TimeBox>
                      <img src={ClockIcon} alt="Duration" />
                      {formatSeconds(replay.videoDuration)}
                    </TimeBox>
                  )}

                  <PlayerBox>
                    <ReactPlayerLoader
                      ref={playerRef}
                      id={'player'}
                      accountId="5454530067001"
                      playerId={process.env.REACT_APP_BRIGHTCOVE_ID}
                      videoId={replay?.brightcoveId}
                      onSuccess={onSuccess}
                      options={{
                        responsive: true,
                        autoplay: cookies.get('bks-autoplay')
                          ? cookies.get('bks-autoplay')
                          : false,
                        muted: false,
                        aspectRatio: '4:3',
                        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
                      }}
                    />
                  </PlayerBox>
                  <TranscriptBox>
                    <Button
                      children={`${replay?.abbr} Community`}
                      color={'whiteBlueBorder'}
                      hoverColor={'primary'}
                      onClick={redirectToCommunity}
                    />
                  </TranscriptBox>
                  <TranscriptBox>
                    <Typography variant={'body2'} color={'#2d2d2d'}>
                      This previously recorded session is being provided for
                      members to offer flexibility in their studying. It will be
                      available until Sunday at midnight.
                    </Typography>
                    <Typography
                      variant={'body2'}
                      color={'#2d2d2d'}
                      marginTop={1}
                    >
                      Please note that this does not replicate the live learning
                      experience. As you watch, we encourage you to take notes,
                      complete the exercises on your own, and pause the video as
                      needed. You can post any follow-up questions to the{' '}
                      <a href="#" onClick={redirectToCommunity}>
                        ARE Community
                      </a>{' '}
                      and tag the instructor.
                    </Typography>
                    <Typography
                      variant={'body2'}
                      color={'#2d2d2d'}
                      marginTop={1}
                    >
                      For an optimal learning experience, we encourage you to
                      participate in the live sessions for opportunities to
                      engage with peers and registered architect coaches.
                    </Typography>
                    <Typography
                      variant={'body2'}
                      color={'#2d2d2d'}
                      marginTop={1}
                    >
                      Your feedback is important and we take it seriously to
                      improve our products. Please take a moment to complete
                      this quick survey about the workshop replay:{' '}
                      <a
                        href="https://forms.gle/kTYnmKxnEJf2Arnh7"
                        target="_blank"
                      >
                        https://forms.gle/kTYnmKxnEJf2Arnh7
                      </a>
                    </Typography>
                    <Typography
                      variant={'body2'}
                      color={'#2d2d2d'}
                      marginTop={'16px'}
                    >
                      {replay?.description}
                    </Typography>
                    <Typography variant={'body2'} marginTop={1}>
                      Prerequisites:
                      {replay && (
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            style={{color: '#999', letterSpacing: 0}}
                          >
                            {' '}
                            (
                            {getNextWorkshopPrerequisitesApproximateDuration(
                              workshopPrereq,
                            )}
                            )
                          </Typography>

                          <Typography
                            component="span"
                            variant={'body2'}
                            marginTop={'8px'}
                            display={'block'}
                            key={uuidv4()}
                          >
                            <Link
                              href={'#'}
                              color={colors.blue1}
                              sx={{textDecoration: 'none'}}
                              onClick={handleDemoClick}
                            >
                              - Getting Started
                            </Link>
                          </Typography>

                          {workshopPrereq?.map(prereq => (
                            <Typography
                              component="span"
                              variant={'body2'}
                              marginTop={'8px'}
                              display={'block'}
                              key={uuidv4()}
                            >
                              <Link
                                href={`${formatPrereqURL(prereq)}`}
                                color={colors.blue1}
                                sx={{textDecoration: 'none'}}
                              >
                                - {formatPrereq(prereq)}
                              </Link>
                            </Typography>
                          ))}
                        </>
                      )}
                    </Typography>
                  </TranscriptBox>
                </>
              )}
            </>
          ) : (
            <>
              <IconsBox>
                <img src={division?.icon} alt={division?.name} />
                <AssetTitle style={{marginTop: 0}}>{replay?.title}</AssetTitle>
              </IconsBox>
              <TimeBox>
                <img src={ClockIcon} alt="Duration" />
                {formatSeconds(replay?.videoDuration ?? 0)}
              </TimeBox>
              <LoggedOutBox>
                <span>You have reached one of our paid content.</span>
                {!hasAccess && auth.isAuthenticated ? (
                  <span>
                    <a
                      href="#"
                      onClick={handleUpgrade(
                        dispatch,
                        auth,
                        member,
                        'virtual workshops replays paywall',
                      )}
                    >
                      <LockIcon /> Upgrade your membership
                    </a>{' '}
                    to get access to this material.
                  </span>
                ) : (
                  <span>
                    You can either <RouterLink to={'/login'}>log in</RouterLink>{' '}
                    to view it or{' '}
                    <a
                      href="https://www.blackspectacles.com/pricing"
                      target="_blank"
                    >
                      learn more about our memberships
                    </a>
                    .
                  </span>
                )}
              </LoggedOutBox>
            </>
          )}
        </RightBox>
        <VirtualWorkshopDemoModal
          demoModalOpen={demoModalOpen}
          setDemoModalOpen={setDemoModalOpen}
        />
      </AssetsBox>
    </>
  )
}

export default View
